import type { FC } from 'react';
import { useCallback, useEffect, useId, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import FreeTextInput from '../FreeTextInput/FreeTextInput.component';
import SearchIcon from 'assets/svg/icons/Search';
import { PinIcon } from 'utils/icons/figmaIcons';
import useGoogleAddressAutocomplete from 'hooks/shared/useGoogleAddressAutocomplete';
import type AddressSearchFieldProps from './AddressSearchField.types';

const getOptionId = (listboxId: string, index: number) =>
  `${listboxId}-option-${index}`;

/**
 * Composite address field with two states:
 *
 * 1. **Search mode** — a single autocomplete input with a search icon.
 *    The user types any address fragment (street, postal code, city…) and
 *    picks a suggestion from the dropdown.
 *
 * 2. **Expanded mode** — renders `children` (the individual form fields
 *    pre-filled by the parent after receiving `onAddressSelect`).
 *
 * The parent controls the transition via the `isExpanded` prop.
 */
const AddressSearchField: FC<AddressSearchFieldProps> = ({
  label,
  searchPlaceholder,
  countryRestrictions,
  includedPrimaryTypes,
  onAddressSelect,
  isExpanded,
  onManualEntry,
  children,
}) => {
  const { t } = useTranslation();
  const listboxId = useId();
  const [searchValue, setSearchValue] = useState('');

  const {
    suggestions,
    isOpen,
    activeIndex,
    handleInputChange,
    handleSuggestionSelect,
    handleKeyDown,
    closeDropdown,
  } = useGoogleAddressAutocomplete({
    onPlaceSelect: onAddressSelect,
    countryRestrictions,
    includedPrimaryTypes,
  });

  const expanded = isOpen && suggestions.length > 0;

  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(getOptionId(listboxId, activeIndex))
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
      handleInputChange(e.target.value);
    },
    [handleInputChange],
  );

  const handleBlur = useCallback(() => {
    setTimeout(closeDropdown, 150);
  }, [closeDropdown]);

  if (isExpanded) {
    return <>{children}</>;
  }

  return (
    <div>
      {label && (
        <h3 className="mb-1 mt-3 text-primary font-semibold">{label}</h3>
      )}
      <div className="relative">
        <FreeTextInput
          value={searchValue}
          onChange={handleChange as any}
          onKeyDown={handleKeyDown as any}
          onBlur={handleBlur}
          placeholder={searchPlaceholder}
          Icon={SearchIcon}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={expanded ? listboxId : undefined}
          aria-activedescendant={
            expanded && activeIndex >= 0
              ? getOptionId(listboxId, activeIndex)
              : undefined
          }
        />

        {expanded && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Address suggestions"
            onMouseDown={(e) => e.preventDefault()}
            className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion, index) => (
              <li
                id={getOptionId(listboxId, index)}
                key={suggestion.placeId}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  void handleSuggestionSelect(suggestion);
                  setSearchValue('');
                }}
                className={classNames(
                  'flex items-start gap-2 px-3 py-2 cursor-pointer text-left',
                  {
                    'bg-gray-100': index === activeIndex,
                    'hover:bg-gray-50': index !== activeIndex,
                  },
                )}
              >
                <PinIcon className="w-5 h-5 mt-0.5 shrink-0 text-gray-400" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900">
                    {suggestion.mainText}
                  </span>
                  {suggestion.secondaryText && (
                    <span className="text-xs text-gray-500">
                      {suggestion.secondaryText}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {expanded
            ? `${suggestions.length} suggestion${
                suggestions.length === 1 ? '' : 's'
              } available`
            : ''}
        </div>
      </div>

      {onManualEntry && (
        <button
          type="button"
          onClick={onManualEntry}
          className="mt-2 text-sm text-bank font-medium hover:underline"
        >
          {t('address.enter_manually')}
        </button>
      )}
    </div>
  );
};

AddressSearchField.displayName = 'AddressSearchField';

export default AddressSearchField;
